import { ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild, HostListener, ChangeDetectionStrategy, inject } from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  TableConfig,
  ModalComponent,
  ModalResult,
  TableComponent,
  PaginatorFetcher
} from '@michelin/theme';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { Store } from '@ngxs/store';
import { FormBuilder, FormGroup, Validators, FormControl, FormArray } from '@angular/forms';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { DealerDataService } from '../../services/dealer-data.service';
import { DealerSetup } from '../../models/dealer-setup.model';
import { PlantSetup } from '../../models/plant-setup.model';
import { PlantTransfer } from '../../models/plant-transfer.model';
import {
  getDisplayOptions,
  getProcessCodesFromDealerSetup,
  enforceThreeDigits,
  buildProcessCodeFields,
  mapYNFieldsToBooleans,
  booleanDealerSetupFields,
  mapBooleansToYN
} from '../../constants/dealer-setup.constants';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { UpdatePosEnabledAction } from '../../../../shared/state/DealerState';
import { map } from 'rxjs';
import { KubeJobsService } from 'src/app/shared/services/kubejobs.service';

@Component({
  selector: 'app-dealer-setup',
  templateUrl: './dealer-setup.component.html',
  styleUrl: './dealer-setup.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealerSetupComponent implements OnInit {
  @ViewChild('deleteConfirmation', { static: true }) deleteModal: ModalComponent;
  @ViewChild('deleteContent') deleteContent: TemplateRef<any>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  @ViewChild('plantTransferTable', { static: false }) tableRef: TableComponent<PlantTransfer>;
  public translationService = inject(TranslationService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public store = inject(Store);
  public fb = inject(FormBuilder);
  public dealerDataService = inject(DealerDataService);
  public kubeJobsService = inject(KubeJobsService);
  public cdr = inject(ChangeDetectorRef);

  productSaveDisabled = true;
  dealerSetupForm: FormGroup;
  loading = true;
  dealerSetupData: DealerSetup;
  processCodes: any[] = [];
  displayOptions = getDisplayOptions(this.translationService);
  panelOpenState = true;
  isPlantTransferPossible = false;
  plants: PlantSetup[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  plantTransferList: PlantTransfer[] = [];
  locallyAddedPlantTransfers: PlantTransfer[] = [];
  isSaveEnabled = false;
  itemToDelete: PlantTransfer | null = null;
  filterFormBackend = this.fb.group({
    originPlantName: [[]],
    destPlantName: [[]]
  });
  private sourceDropdownCache: string[] | null = null;
  private basePlantTransferList: PlantTransfer[] = [];
  private modifiedPlantTransfers: Record<string, Partial<PlantTransfer>> = {};
  private dealerSetupBaseline: any = null;
  bypassCanDeactivate = false;
  discardMessage = '';

  tableConfig: TableConfig<PlantTransfer> = {
    paginator: { type: 'scroll', pageSize: 50 },
    columns: {}
  };

  fetcher: PaginatorFetcher<PlantTransfer> = () =>
    this.dealerDataService.getAllPlantTransfer(this.filterFormBackend.value).pipe(
      map((plantTransfers: PlantTransfer[]) => {
        const baseline = (plantTransfers || []).map((pt) => ({ ...pt, newRowFlag: false }));
        this.basePlantTransferList = baseline;
        this.plantTransferList = this.applyLocalModifications(baseline);
        const combined = [...this.locallyAddedPlantTransfers, ...this.plantTransferList];
        this.updateDisplayedRowsForm(combined);
        return { items: combined, count: combined.length };
      })
    );

  constructor() {}

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);

    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText')
      } as Alert);
    }
    this.initDealerSetupForm();
    this.loadDealerSetupData();
    this.displayedRowsForm.valueChanges.subscribe(this.checkIfModified.bind(this));
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.updateTableColumns();
      this.cdr.markForCheck();
    });
    this.initializeForm();
  }

  private updateTableColumns(): void {
    this.tableConfig.columns = {
      originPlantName: { header: this.translationService.translate('sourcePlantText'), cell: 'originPlantName' },
      destPlantName: { header: this.translationService.translate('destinationPlantText'), cell: 'destPlantName' },
      bothWay: { header: this.translationService.translate('transferBothWaysText'), cell: 'bothWay' },
      action: { header: this.translationService.translate('actionText'), cell: 'action' }
    };
  }

  private initDealerSetupForm(): void {
    this.dealerSetupForm = this.fb.group({
      posEnable: [false],
      barCodeLeadingZero: [false],
      overrideGradeEditable: [false],
      mfgDotReqOnWo: [false],
      displayDeleteCasing: [false],
      dueDateOverrideLimit: ['', [Validators.required, Validators.maxLength(3)]],
      optDate1: [''],
      optDate1Lable: [''],
      optDate2: [''],
      optDate2Lable: [''],
      optText1: [''],
      optText1Label: [''],
      woBackDtLimit: ['', [Validators.required, Validators.maxLength(3)]],
      markWoAvailable: [false],
      optText2: [''],
      optText2Label: [''],
      optTableLookup: [''],
      autoShipListNumbering: [false],
      autoNumberingPlantFormat: [''],
      autoNumberingSiteFormat: [''],
      pwdExpireEvery: ['', [Validators.required, Validators.maxLength(3)]],
      minPwdLength: ['', [Validators.required, Validators.min(10), Validators.max(99), this.minPwdLengthValidator()]],
      btmAllowBulkEntry: [false],
      btmAllowPickupChange: [false],
      btmAllowModifyDueDate: [false],
      btmAutoGenerateWo: [false],
      btmAutoGeneratePlNum: [false],
      btmAllowCreateWo: [false],
      originPlantName: ['All'],
      destPlantName: ['All'],
      isPlantTransferPossible: [false],
      plants: [[]]
    });
  }

  loadDealerSetupData(): void {
    this.dealerDataService.getDealerSetup().subscribe({
      next: ({ data }) => {
        if (data) {
          this.dealerSetupData = data;
          this.isPlantTransferPossible = data.plantTransferPossible;
          this.plants = data.plants || [];
          this.clearDropdownCache();
          this.populateFormWithApiData(data);
        }
      },
      error: () => {
        console.error('Error loading dealer setup data');
        this.alertService.showAlert({
          type: AlertType.strong,
          content: 'Failed to load dealer setup data. Please try again.'
        } as Alert);
      }
    });
  }

  populateFormWithApiData(data: DealerSetup): void {
    this.dealerSetupForm.patchValue({
      ...mapYNFieldsToBooleans(data, booleanDealerSetupFields),
      dueDateOverrideLimit: data.dueDateOverrideLimit,
      optDate1: data.optDate1,
      optDate1Lable: data.optDate1Label,
      optDate2: data.optDate2,
      optDate2Lable: data.optDate2Label,
      optText1: data.optText1,
      optText1Label: data.optText1Label,
      woBackDtLimit: data.woBackDtLimit,
      optText2: data.optText2,
      optText2Label: data.optText2Label,
      optTableLookup: data.optTableLookup,
      autoNumberingPlantFormat: data.autoNumberingPlantFormat,
      autoNumberingSiteFormat: data.autoNumberingSiteFormat,
      pwdExpireEvery: data.pwdExpireEvery,
      minPwdLength: data.minPwdLength,
      isPlantTransferPossible: data.plantTransferPossible,
      plants: data.plants || []
    });
    this.processCodes = getProcessCodesFromDealerSetup(data, this.translationService);
    Promise.resolve().then(() => {
      this.dealerSetupBaseline = this.buildDealerSetupComparable();
      this.dealerSetupForm.markAsPristine();
    });
  }

  /**
   * Validates the dealer setup form and shows an alert if invalid.
   * Returns true if the form is valid, false otherwise.
   */
  private validateDealerSetupForm(): boolean {
    if (this.productSaveDisabled) {
      return false;
    }
    this.dealerSetupForm.markAllAsTouched();
    if (!this.dealerSetupForm.valid) {
      this.showAlert(AlertType.strong, this.translationService.translate('fillAllMandatoryFieldText')); // Please complete all required fields before saving.
      return false;
    }
    return true;
  }

  /**
   * Handles saving dealer setup data.
   * Validates form, builds payload, and calls API.
   */
  save(): void {
    if (!this.validateDealerSetupForm()) return;

    const payload = this.buildDealerSetupPayload();
    this.dealerDataService.updateDealerSetup(payload).subscribe({
      next: ({ data }) => {
        this.dealerSetupData = data;
        this.kubeJobsService.updateCronSchedules(data);
        this.dealerSetupForm.markAsPristine();
        this.dealerSetupBaseline = this.buildDealerSetupComparable();
        this.isSaveEnabled = false; // reset unsaved state after save
        this.modifiedPlantTransfers = {}; // ensure no stale modifications linger
        const posEnabled = this.dealerSetupForm.get('posEnable').value === true;
        this.store.dispatch(new UpdatePosEnabledAction(posEnabled));
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('dealerSetupSaveText')
        } as Alert);
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  private initializeForm(): void {
    this.updateDisplayedRowsForm(this.plantTransferList);
  }

  // Wrapper method for enforceThreeDigits
  onEnforceThreeDigits(event: any): void {
    enforceThreeDigits(event, this.dealerSetupForm);
  }

  /**
   * Builds the dealer setup payload for API submission.
   * Converts form values to the correct format and structure.
   * @returns The payload object for updating dealer setup.
   */
  private buildDealerSetupPayload(): any {
    const formValues = mapBooleansToYN(this.dealerSetupForm.value, booleanDealerSetupFields);
    return {
      ...formValues,
      ...buildProcessCodeFields(this.processCodes)
    };
  }

  updateDisplayedRowsForm(items: PlantTransfer[]): void {
    this.displayedRowsForm.setControl('rows', this.fb.array(items.map((row) => this.createRowForm(row))));
  }

  createRowForm(row: PlantTransfer): FormGroup {
    return this.fb.group({
      plantTransferPk: [row.plantTransferPk],
      dealerFk: [row.dealerFk],
      originPlantFk: [row.originPlantFk],
      destPlantFk: [row.destPlantFk],
      originPlantName: [row.originPlantName],
      destPlantName: [row.destPlantName],
      bothWay: [row.bothWay ?? 'N'],
      newRowFlag: [!!row.newRowFlag]
    });
  }

  /**
   * Custom validator for minPwdLength to ensure value is between 10 and 99.
   */
  private minPwdLengthValidator(): any {
    return (control: any) => {
      const value = +control.value;
      return value >= 10 && value <= 99 ? null : { minPwdLength: this.translationService.translate('minPasswordLengthText') };
    };
  }

  getControl(item: PlantTransfer, controlName: keyof PlantTransfer): FormControl {
    const index = [...this.locallyAddedPlantTransfers, ...this.plantTransferList].findIndex(
      (plantTransfer) => plantTransfer.plantTransferPk === item.plantTransferPk
    );

    if (index === -1 || !(this.displayedRowsForm.get('rows') as FormArray)?.at(index)) {
      return new FormControl('');
    }

    return ((this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl) || new FormControl('');
  }

  /**
   * Get source plant list for dropdown with "All" option (with caching)
   * Returns plant names as both label and value for proper matching
   */
  getPlantList(): string[] {
    if (this.sourceDropdownCache?.length) {
      return this.sourceDropdownCache;
    }
    const allValue = this.translationService.translate('allText') || 'All';
    this.sourceDropdownCache = [allValue, ...this.plants.map((plant) => plant.name)];
    return this.sourceDropdownCache;
  }

  /**
   * Clear dropdown cache when plants data changes
   */
  private clearDropdownCache(): void {
    this.sourceDropdownCache = null;
  }

  /**
   * Check if plant transfer form has been modified
   */
  checkIfModified(): void {
    const processCodesChanged = this.checkIfModifiedProcessCodes();
    const hasLocalChanges = this.locallyAddedPlantTransfers.length > 0 || Object.keys(this.modifiedPlantTransfers).length > 0;
    if (hasLocalChanges || processCodesChanged) {
      this.isSaveEnabled = !this.productSaveDisabled;
      return;
    }

    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    const combined = [...this.locallyAddedPlantTransfers, ...this.plantTransferList];
    this.isSaveEnabled =
      combined.some((original, i) => {
        const current = formArray.at(i).value;
        return (
          (current.originPlantName || current.destPlantName || current.bothWay) &&
          Object.keys(current).some((key) => current[key] !== original[key])
        );
      }) && !this.productSaveDisabled;
  }

  onBothWayChanged(item: PlantTransfer, checked: boolean): void {
    const newValue = checked ? 'Y' : 'N';
    const isNew = !!item.newRowFlag;

    if (!isNew) {
      const baseOriginal = this.basePlantTransferList.find((b) => b.plantTransferPk === item.plantTransferPk);
      if (baseOriginal?.bothWay === newValue) {
        delete this.modifiedPlantTransfers[item.plantTransferPk];
      } else {
        this.modifiedPlantTransfers[item.plantTransferPk] = { ...this.modifiedPlantTransfers[item.plantTransferPk], bothWay: newValue };
      }
    }

    const updateList = (list: PlantTransfer[]) => {
      const index = list.findIndex((pt) => pt.plantTransferPk === item.plantTransferPk);
      if (index !== -1) list[index].bothWay = newValue;
    };

    updateList(this.locallyAddedPlantTransfers);
    updateList(this.plantTransferList);

    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (formArray?.length) {
      const combined = [...this.locallyAddedPlantTransfers, ...this.plantTransferList];
      const formIndex = combined.findIndex((pt) => pt.plantTransferPk === item.plantTransferPk);
      formArray.at(formIndex)?.get('bothWay')?.setValue(newValue, { emitEvent: false });
    }

    this.checkIfModified();
    this.cdr.markForCheck();
  }

  /**
   * Add new plant transfer row - refreshed implementation mirroring casing grade pattern
   */
  async addPlantTransferRow(): Promise<void> {
    this.syncFormToDataModel();
    if (this.productSaveDisabled) {
      return;
    }
    this.dealerDataService.getNewDealerPlantTransferPk().subscribe({
      next: (response: any) => {
        const newId = response?.data;
        const newRow: PlantTransfer = {
          plantTransferPk: newId,
          dealerFk: this.plants[0]?.dealerFk || '',
          originPlantFk: '',
          destPlantFk: '',
          bothWay: 'N',
          originPlantName: this.translationService.translate('allText') || 'All',
          destPlantName: this.translationService.translate('allText') || 'All',
          newRowFlag: true
        };

        this.locallyAddedPlantTransfers.unshift(newRow);
        this.updateDisplayedRowsForm([...this.locallyAddedPlantTransfers, ...this.plantTransferList]);
        this.tableRef?.resetFilters();
        this.tableRef?.patchExtraFilters({}, true);
        this.isSaveEnabled = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  private syncFormToDataModel(): void {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (!formArray) return;
    const combinedItems = [...this.locallyAddedPlantTransfers, ...this.plantTransferList];
    for (let i = 0; i < formArray.length; i++) {
      const formGroup = formArray.at(i) as FormGroup;
      if (formGroup && combinedItems[i]) {
        Object.assign(combinedItems[i], formGroup.value);
      }
    }
  }

  /**
   * Delete plant transfer row - Fixed to use proper table refresh methods
   */
  deletePlantTransferRow(item: PlantTransfer): void {
    if (this.productSaveDisabled) return;

    delete this.modifiedPlantTransfers[item.plantTransferPk];

    const locallyAddedIndex = this.locallyAddedPlantTransfers.findIndex(
      (plantTransfer) => plantTransfer.plantTransferPk === item.plantTransferPk
    );

    if (locallyAddedIndex !== -1) {
      this.locallyAddedPlantTransfers.splice(locallyAddedIndex, 1);
      this.showSuccessAlert('plantTransferDeleteText');
      this.refreshTable();
      return;
    }

    this.dealerDataService.deleteDealerPlantTransfer(item.plantTransferPk).subscribe({
      next: () => {
        this.plantTransferList = this.plantTransferList.filter((row) => row.plantTransferPk !== item.plantTransferPk);
        this.showSuccessAlert('plantTransferDeleteText');
        this.refreshTable();
        this.isSaveEnabled = false;
      },
      error: () => this.showErrorAlert('plantTransferDeleteText')
    });
  }

  private showSuccessAlert(translationKey: string): void {
    this.alertService.showAlert({
      type: AlertType.success,
      content: this.translationService.translate(translationKey)
    } as Alert);
  }

  private showErrorAlert(translationKey: string): void {
    this.alertService.showAlert({
      type: AlertType.critical,
      content: this.translationService.translate(translationKey)
    } as Alert);
  }

  private refreshTable(): void {
    this.updateDisplayedRowsForm([...this.locallyAddedPlantTransfers, ...this.plantTransferList]);
    this.tableRef.resetFilters();
    this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    this.checkIfModified();
  }

  /**
   * Updated showDeleteModal method
   */
  showDeleteModal(item: PlantTransfer): void {
    this.deleteModal.open({
      id: 'deleteConfirmation',
      title: this.translationService.translate('warningText'),
      width: 550,
      content: this.deleteContent,
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText')
    });
    this.itemToDelete = item;
  }

  /**
   * Handle delete modal result
   */
  onDeleteModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'deleteConfirmation' && result.result) {
        this.deletePlantTransferRow(this.itemToDelete!);
      }
      this.itemToDelete = null;
    }
  }

  /**
   * Show discard changes modal
   */
  showDiscardModal(): void {
    this.discardMessage = this.buildDiscardMessage();
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  /**
   * Handle discard modal result
   */
  onDiscardModalClosed(result: ModalResult): void {
    if (result && result.id === 'discardConfirmation' && result.result) {
      this.locallyAddedPlantTransfers = [];
      this.isSaveEnabled = false;
    }
  }

  /** Centralized unsaved changes detection */
  private hasUnsavedChanges(): boolean {
    const unsavedDealer = this.unsavedDealerSetup();
    const unsavedPlant = this.unsavedPlantTransfer();
    return unsavedDealer || unsavedPlant;
  }

  private unsavedDealerSetup(): boolean {
    return (this.dealerSetupForm?.dirty && this.dealerSetupFormChanged()) || this.checkIfModifiedProcessCodes();
  }

  private unsavedPlantTransfer(): boolean {
    if (this.locallyAddedPlantTransfers.length > 0 || Object.keys(this.modifiedPlantTransfers).length > 0) return true;
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (!formArray) return false;
    const baselineMap = new Map(this.basePlantTransferList.map((r) => [r.plantTransferPk, r]));
    return formArray.value.some((row: PlantTransfer) => {
      const original = baselineMap.get(row.plantTransferPk);
      return (
        row.newRowFlag ||
        !original ||
        row.originPlantName !== original.originPlantName ||
        row.destPlantName !== original.destPlantName ||
        (row.bothWay || 'N') !== (original.bothWay || 'N')
      );
    });
  }

  private buildDiscardMessage(): string {
    const dealerTxt = this.translationService.translate('dealerSetupUnsavedText');
    const plantTxt = this.translationService.translate('plantTransferUnsavedText');
    const bothTxt = this.translationService.translate('dealerAndPlantTransferUnsavedText');

    const d = this.unsavedDealerSetup();
    const p = this.unsavedPlantTransfer();
    if (d && p) return bothTxt;
    if (d) return dealerTxt;
    if (p) return plantTxt;
    return this.translationService.translate('discardChangesText');
  }

  canDeactivate(): Promise<boolean> {
    if (this.bypassCanDeactivate || this.productSaveDisabled) {
      return Promise.resolve(true);
    }

    if (this.hasUnsavedChanges()) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const sub = this.discardModal.result.subscribe((result: ModalResult) => {
          const proceed = result?.id === 'discardConfirmation' && !!result.result;
          if (proceed) {
            this.resetUnsavedState();
          }
          resolve(proceed);
          sub.unsubscribe();
        });
      });
    }

    return Promise.resolve(true);
  }

  private resetUnsavedState(): void {
    this.locallyAddedPlantTransfers = [];
    this.modifiedPlantTransfers = {};
    this.isSaveEnabled = false;
    this.dealerSetupForm?.markAsPristine();
    this.displayedRowsForm?.markAsPristine();
  }

  /**
   * Handle browser refresh/close with unsaved changes
   */
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.isSaveEnabled) {
      $event.returnValue = this.translationService.translate('discardChangesText');
    }
  }

  savePlantTransfer(): void {
    if (this.productSaveDisabled) return;

    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (!formArray) return;

    const rows: PlantTransfer[] = formArray.value;
    const allValue = this.translationService.translate('allText') || 'All';
    const nameToId = (name: string): string => (name && name !== allValue ? this.plants.find((p) => p.name === name)?.id || '' : '');

    if (this.hasDuplicateAllRows(rows, allValue)) {
      this.showAlert(AlertType.strong, this.translationService.translate('duplicateAllPlantTransferText'));
      return;
    }

    const invalidSamePairs = rows.filter(
      (r) => r.originPlantName && r.destPlantName && r.originPlantName === r.destPlantName && r.originPlantName !== allValue
    );
    if (invalidSamePairs.length > 0) {
      this.showAlert(AlertType.strong, this.translationService.translate('invalidOriginPlantText'));
      return;
    }

    const payload = this.buildPayload(rows, allValue, nameToId);
    if (!payload.length) {
      this.showAlert(AlertType.classical, this.translationService.translate('noChangesToSaveText'));
      return;
    }

    this.dealerDataService.updatePlantTransfer(payload).subscribe({
      next: () => {
        this.showAlert(AlertType.success, this.translationService.translate('plantTransferSaveText'));
        // Adopt current displayed rows as new baseline
        const formRows = (this.displayedRowsForm.get('rows') as FormArray).value as PlantTransfer[];
        this.basePlantTransferList = formRows.map((r) => ({ ...r, newRowFlag: false }));
        this.plantTransferList = this.basePlantTransferList.map((r) => ({ ...r }));
        this.locallyAddedPlantTransfers = [];
        this.modifiedPlantTransfers = {};
        this.isSaveEnabled = false;
        this.updateDisplayedRowsForm(this.plantTransferList); // refresh form controls without new row flags
        this.displayedRowsForm.markAsPristine();
        this.cdr.markForCheck();
      },
      error: () => {
        this.showAlert(AlertType.critical, this.translationService.translate('criticalErrorAlertText'));
      }
    });
  }

  private hasDuplicateAllRows(rows: PlantTransfer[], allValue: string): boolean {
    return rows.some(
      (row, i) =>
        row.originPlantName === allValue &&
        row.destPlantName === allValue &&
        rows.some((other, j) => i !== j && other.originPlantName === allValue && other.destPlantName === allValue)
    );
  }

  private buildPayload(rows: PlantTransfer[], allValue: string, nameToId: (name: string) => string): PlantTransfer[] {
    const combinedOriginal = [...this.locallyAddedPlantTransfers, ...this.basePlantTransferList];
    return rows
      .map((current, i) => {
        const original = combinedOriginal[i];
        const isNew = current.newRowFlag === true;

        // (Validation for same origin/destination moved to savePlantTransfer to avoid multiple alerts)
        const originPlantFk = nameToId(current.originPlantName) || allValue;
        const destPlantFk = nameToId(current.destPlantName) || allValue;

        const isChanged =
          isNew ||
          (original &&
            (originPlantFk !== (original.originPlantFk || allValue) ||
              destPlantFk !== (original.destPlantFk || allValue) ||
              current.bothWay !== original.bothWay));

        return isChanged ? { ...current, originPlantFk, destPlantFk } : null;
      })
      .filter((row) => row);
  }

  private showAlert(type: AlertType, content: string): void {
    this.alertService.showAlert({ type, content } as Alert);
  }

  private applyLocalModifications(baseList: PlantTransfer[]): PlantTransfer[] {
    return baseList.map((row) => {
      const mods = this.modifiedPlantTransfers[row.plantTransferPk];
      return mods ? { ...row, ...mods, newRowFlag: false } : { ...row, newRowFlag: false };
    });
  }

  private buildProcessCodesHash(): string {
    return JSON.stringify(
      (this.processCodes || [])
        .map((pc: any) => ({ code: pc.code ?? pc.processCode ?? pc.id, checked: !!pc.checked }))
        .sort((a, b) => a.code.localeCompare(b.code))
    );
  }

  private checkIfModifiedProcessCodes(): boolean {
    if (!this.dealerSetupBaseline) return false;
    return this.dealerSetupBaseline.processCodesHash !== this.buildProcessCodesHash();
  }

  private buildDealerSetupComparable(): any {
    if (!this.dealerSetupForm) return null;

    const formValues = this.dealerSetupForm.getRawValue();
    return {
      ...formValues,
      plantsLength: Array.isArray(formValues.plants) ? formValues.plants.length : 0,
      processCodesHash: this.buildProcessCodesHash()
    };
  }

  private dealerSetupFormChanged(): boolean {
    if (!this.dealerSetupBaseline) return false;
    const current = this.buildDealerSetupComparable();
    return JSON.stringify(current) !== JSON.stringify(this.dealerSetupBaseline);
  }

  // New method: handle process code checkbox change to enable unsaved changes detection
  onProcessCodeChanged(code: any, checked: boolean): void {
    if (code) {
      code.checked = checked;
      this.isSaveEnabled = this.checkIfModifiedProcessCodes() && !this.productSaveDisabled;
      this.checkIfModified();
      this.cdr.markForCheck();
    }
  }
}
